import { Input, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterPersonality } from '@creanizer/types'

interface PersonalityEditorProps {
  personality: CharacterPersonality
  onChange: (personality: CharacterPersonality) => void
}

export function PersonalityEditor({
  personality,
  onChange
}: PersonalityEditorProps) {
  const [newItems, setNewItems] = useState({
    traits: '',
    strengths: '',
    weaknesses: '',
    fears: '',
    desires: '',
    values: '',
    likes: '',
    dislikes: '',
    goals: ''
  })

  const handleAdd = (field: keyof CharacterPersonality) => {
    if (newItems[field as keyof typeof newItems]) {
      onChange({
        ...personality,
        [field]: [...(personality[field] || []), newItems[field as keyof typeof newItems]]
      })
      setNewItems(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleRemove = (field: keyof CharacterPersonality, index: number) => {
    onChange({
      ...personality,
      [field]: (personality[field] || []).filter((_: string, i: number) => i !== index)
    })
  }

  const handleUpdate = (field: keyof CharacterPersonality, index: number, value: string) => {
    const updated = [...(personality[field] || [])]
    updated[index] = value
    onChange({
      ...personality,
      [field]: updated
    })
  }

  const renderSection = (
    title: string,
    field: keyof CharacterPersonality,
    placeholder: string
  ) => (
    <div>
      <Label className="font-medium">{title}</Label>
      <div className="space-y-2 mt-2">
        {(personality[field] || []).map((item: string, index: number) => (
          <div key={index} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => handleUpdate(field, index, e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRemove(field, index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input
            value={newItems[field as keyof typeof newItems] || ''}
            onChange={(e) => setNewItems(prev => ({ ...prev, [field]: e.target.value }))}
            placeholder={placeholder}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd(field)}
            className="flex-1"
          />
          <Button
            size="sm"
            onClick={() => handleAdd(field)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {renderSection('性格特性', 'traits', '例: 勇敢')}
      {renderSection('長所', 'strengths', '例: リーダーシップがある')}
      {renderSection('短所・弱点', 'weaknesses', '例: 頑固')}
      {renderSection('恐れ', 'fears', '例: 孤独')}
      {renderSection('欲求・願望', 'desires', '例: 認められたい')}
      {renderSection('価値観', 'values', '例: 正義')}
      {renderSection('好きなもの', 'likes', '例: 読書、紅茶')}
      {renderSection('嫌いなもの', 'dislikes', '例: 嘘、暴力')}
      {renderSection('夢・目標', 'goals', '例: 世界平和を実現する')}
    </div>
  )
}